import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { serializeError } from 'serialize-error';

import typeCheck from 'common/util/typeCheck';

import {
  SR_API_ACTION_SUNLOCK_GET_LIST,
  SR_API_ACTION_SUNLOCK_ASSIGN_LISTING_ID,
  SR_API_ACTION_SUNLOCK_UNASSIGN_LISTING_ID,
  SR_API_ACTION_SUNLOCK_UPDATE_LOCK_CODES,
  SR_API_ACTION_SUNLOCK_SEND_COMMAND,
  SR_API_ACTION_SUNLOCK_UPDATE_STATUS,
} from 'api/actionTypes';

import {
  SCREEN_ID_SUNLOCKS_DASHBOARD,
} from 'client/constants/screenIds';

import {
  USER_ROLE_ADMIN,
} from 'common/userRoles';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withApiState from 'client/helpers/withApiState';
import withScreenId from 'client/helpers/withScreenId';
import allowedRoles from 'client/helpers/allowedRoles';
import withScrollToTop from 'client/helpers/withScrollToTop';

import log from 'client/lib/log';
import branch from 'client/core/branch';
import SunlockActions from 'client/actions/Sunlock';
import postWithState from 'client/actions/postWithState';

import Navbar from 'client/components/Navbar';
import SrSunlockRegisterDeviceForm from 'client/components/SrSunlockRegisterDeviceForm';
import SrSunlockDebugForm from 'client/components/SrSunlockDebugForm';
import SrTable from 'client/components/SrTable';

import {
  SUNLOCK_STATUS_READY,
  SUNLOCK_STATUS_ACTIVE,
  SUNLOCK_STATUS_INACTIVE,
  SUNLOCK_STATUS_AVAILABLE_TRANSITIONS,
  SUNLOCK_COMMAND_LOCK_DOOR,
  SUNLOCK_COMMAND_UNLOCK_DOOR,
} from 'common/sunlocks/sunlockConstants';

@withScrollToTop
@withTransitionHook
@allowedRoles({
  roles: [
    USER_ROLE_ADMIN,
  ],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withApiState({
  getSunlocksListApiState: {
    action: SR_API_ACTION_SUNLOCK_GET_LIST,
  },
})
@withScreenId(SCREEN_ID_SUNLOCKS_DASHBOARD)
@branch({
  sunlocksList: ['sunlocks', 'list'],
  currentUser: ['currentUser'],
})
class SunlocksDashboardPage extends Component {
  static fetchData(state, inject) {
    return SunlockActions.getSunlocksList(inject);
  }

  static getPageMetadata = () => ({
    pageTitle: 'Sunlocks | Sunroom Rentals',
  });

  static propTypes = {
    sunlocksList: PropTypes.array,
    getSunlocksListApiState: PropTypes.object.isRequired,
    currentUser: PropTypes.object.isRequired,
  }

  constructor(props) {
    super(props);
    this.state = {
      isBusy: false,
      apiResponse: null,
      apiError: null,
    };
    this._dispatchSunlockAction = this._dispatchSunlockAction.bind(this);
  }

  async _dispatchSunlockAction({ action, payload }) {
    const { dispatch } = this.props;

    try {
      typeCheck('action::NonEmptyString', action);
      typeCheck('payload::Maybe Object', payload);

      this.setState({
        isBusy: true,
        apiResponse: null,
        apiError: null,
      });

      const apiResponse = await dispatch(postWithState, { action, payload });

      await dispatch(SunlockActions.getSunlocksList);

      this.setState({
        isBusy: false,
        apiResponse: apiResponse,
        apiError: null,
      });
    }
    catch (error) {
      log.error(error);
      this.setState({
        isBusy: false,
        apiResponse: null,
        apiError: error,
      });
    }
  }

  _onUnassignListingIdClick = (event) => {
    const { currentUser } = this.props;
    if (!global.confirm('Are you sure you want to unassign this Sunlock\'s listing?')) {
      return;
    }
    const sunlockId = parseInt(event.target.dataset.sunlockId, 10);
    this._dispatchSunlockAction({
      action: SR_API_ACTION_SUNLOCK_UNASSIGN_LISTING_ID,
      payload: {
        triggeredBy: currentUser.name,
        sunlockId,
      },
    });
  }

  _onAssignListingIdClick = (event) => {
    const { currentUser } = this.props;
    const listingIdString = global.prompt(
      'Please enter the listing ID you want to assign to this device.'
    );
    if (!listingIdString) {
      return;
    }
    const listingId = parseInt(listingIdString, 10);
    if (!listingId || isNaN(listingId)) {
      global.alert(`"${listingIdString}" is not a valid listing ID.`);
      return;
    }
    const sunlockId = parseInt(event.target.dataset.sunlockId, 10);
    this._dispatchSunlockAction({
      action: SR_API_ACTION_SUNLOCK_ASSIGN_LISTING_ID,
      payload: {
        triggeredBy: currentUser.name,
        sunlockId,
        listingId,
      },
    });
  }

  _onStopSunlockClick = (event) => {
    if (!global.confirm('Are you sure you want to stop this Sunlock?')) {
      return;
    }
    const sunlockId = parseInt(event.target.dataset.sunlockId, 10);
    this._dispatchSunlockAction({
      action: SR_API_ACTION_SUNLOCK_UPDATE_STATUS,
      payload: {
        sunlockId: sunlockId,
        status: SUNLOCK_STATUS_INACTIVE,
      },
    });
  }

  _onMarkSunlockAsReadyClick = (event) => {
    if (!global.confirm('Are you sure you want to start this Sunlock?')) {
      return;
    }
    const sunlockId = parseInt(event.target.dataset.sunlockId, 10);
    this._dispatchSunlockAction({
      action: SR_API_ACTION_SUNLOCK_UPDATE_STATUS,
      payload: {
        sunlockId: sunlockId,
        status: SUNLOCK_STATUS_READY,
      },
    });
  }

  _onUpdateLockCodeClick = (event) => {
    const { currentUser } = this.props;
    const lockCode = global.prompt(
      'If you updated the lock code manually, please enter the new code here.'
    );
    if (!lockCode) {
      return;
    }
    const sunlockId = parseInt(event.target.dataset.sunlockId, 10);
    this._dispatchSunlockAction({
      action: SR_API_ACTION_SUNLOCK_UPDATE_LOCK_CODES,
      payload: {
        triggeredBy: currentUser.name,
        sunlockId,
        lockCode,
      },
    });
  }

  _onUnlockDoorClick = (event) => {
    if (!global.confirm('Are you sure you want to unlock this door?')) {
      return;
    }
    const sunlockId = parseInt(event.target.dataset.sunlockId, 10);
    this._dispatchSunlockAction({
      action: SR_API_ACTION_SUNLOCK_SEND_COMMAND,
      payload: {
        sunlockId: sunlockId,
        sunlockCommand: SUNLOCK_COMMAND_UNLOCK_DOOR,
      },
    });
  }

  _onLockDoorClick = (event) => {
    if (!global.confirm('Are you sure you want to lock this door?')) {
      return;
    }
    const sunlockId = parseInt(event.target.dataset.sunlockId, 10);
    this._dispatchSunlockAction({
      action: SR_API_ACTION_SUNLOCK_SEND_COMMAND,
      payload: {
        sunlockId: sunlockId,
        sunlockCommand: SUNLOCK_COMMAND_LOCK_DOOR,
      },
    });
  }

  render() {
    const {
      sunlocksList,
      getSunlocksListApiState,
    } = this.props;

    const {
      isBusy,
      apiResponse,
      apiError,
    } = this.state;

    return (
      <div className='page-admin'>
        <div className='adminContainer'>
          <Navbar redirectLogoTo='/admin' />

          <div className='section'>
            {isBusy || getSunlocksListApiState.isBusy || !sunlocksList
              ? <div className='loading-gif' />
              : (
                <div>
                  {apiResponse && (
                    <div>
                      Success:
                      <pre>
                        {JSON.stringify(apiResponse, null, 2)}
                      </pre>
                    </div>
                  )}
                  {apiError && (
                    <div>
                      Error:
                      <pre>
                        {JSON.stringify(serializeError(apiError), null, 2)}
                      </pre>
                    </div>
                  )}

                  <SrTable
                    headers={[
                      { content: 'ID' },
                      { content: 'Status' },
                      { content: 'Created' },
                      { content: 'Serial Number' },
                      { content: 'Last Ping Received' },
                      { content: 'Lock Code' },
                      { content: 'Listing' },
                      { content: 'Actions' },
                    ]}
                    rows={sunlocksList.map((sunlock) => {
                      const sunlockAvailableTransitions = (
                        SUNLOCK_STATUS_AVAILABLE_TRANSITIONS[sunlock.status]
                      );

                      return {
                        cells: [
                          { content: sunlock.id },
                          { content: sunlock.status },
                          { content: moment(sunlock.createdDate).format('ll') },
                          { content: sunlock.raspberryPiSerialNumber },
                          { content: sunlock.lastPingDate
                            ? moment(sunlock.lastPingDate).format('MM/DD h:mm A')
                            : '--',
                          },
                          { content: sunlock.lockCode },
                          { content: sunlock.listingId
                            ? `${sunlock.listingId} - ${sunlock.listingAddressStreetDisplay}`
                            : '--',
                          },
                          {
                            content: (
                              <div>
                                {sunlock.listingId
                                  ? (
                                    <div
                                      data-sunlock-id={sunlock.id}
                                      onClick={this._onUnassignListingIdClick}
                                    >
                                      Unassign Listing
                                    </div>
                                  )
                                  : (
                                    <div
                                      data-sunlock-id={sunlock.id}
                                      onClick={this._onAssignListingIdClick}
                                    >
                                      Assign Listing
                                    </div>
                                  )
                                }
                                {sunlockAvailableTransitions.includes(SUNLOCK_STATUS_INACTIVE) && (
                                  <div
                                    data-sunlock-id={sunlock.id}
                                    onClick={this._onStopSunlockClick}
                                  >
                                    Stop
                                  </div>
                                )}
                                {
                                  sunlockAvailableTransitions.includes(SUNLOCK_STATUS_READY)
                                  && sunlock.listingId
                                  && (
                                    <div
                                      data-sunlock-id={sunlock.id}
                                      onClick={this._onMarkSunlockAsReadyClick}
                                    >
                                      Start
                                    </div>
                                  )
                                }
                                {
                                  sunlockAvailableTransitions.includes(SUNLOCK_STATUS_READY)
                                  && sunlock.listingId
                                  && (
                                    <div
                                      data-sunlock-id={sunlock.id}
                                      onClick={this._onUpdateLockCodeClick}
                                    >
                                      New lock code?
                                    </div>
                                  )
                                }
                                {sunlock.status === SUNLOCK_STATUS_ACTIVE && (
                                  <div
                                    data-sunlock-id={sunlock.id}
                                    onClick={this._onUnlockDoorClick}
                                  >
                                    Unlock Door
                                  </div>
                                )}
                                {sunlock.status === SUNLOCK_STATUS_ACTIVE && (
                                  <div
                                    data-sunlock-id={sunlock.id}
                                    onClick={this._onLockDoorClick}
                                  >
                                    Lock Door
                                  </div>
                                )}
                              </div>
                            ),
                          },
                        ],
                      };
                    })}
                  />
                  <SrSunlockRegisterDeviceForm />
                  <SrSunlockDebugForm sunlocksList={sunlocksList} />
                </div>
              )
            }
          </div>
        </div>
      </div>
    );
  }
}

SunlocksDashboardPage.displayName = 'SunlocksDashboardPage';

export default SunlocksDashboardPage;
